import { RULER_FIELDS, type RulerField } from './convert';
import { cn } from '@/lib/cn';

/**
 * A payee record, cut at the positions Publication 1220 puts its fields at.
 *
 * This is the thing a FIRE file actually is: 750 characters where meaning comes from where a
 * character sits. Nobody who has to debug one of these files gets to see it laid out, so the
 * page shows it: the position, the field, the characters that are really there, and for the
 * fields that matter, what IRIS calls the same fact.
 *
 * With no `line` it is the empty layout, which is a fair picture of the format itself.
 */
export function RecordRuler({ line, className }: { line?: string | null; className?: string }) {
  return (
    <div className={cn('overflow-x-auto pb-2', className)}>
      <div className="flex min-w-max items-stretch gap-1">
        {RULER_FIELDS.map((field, index) => (
          <Segment key={field.key || `gap-${index}`} field={field} line={line ?? null} />
        ))}
      </div>
    </div>
  );
}

function Segment({ field, line }: { field: RulerField; line: string | null }) {
  if (field.gap) {
    return (
      <div className="flex w-8 shrink-0 flex-col justify-end pb-6 text-center text-muted-foreground" aria-hidden="true">
        <span className="text-sm">···</span>
      </div>
    );
  }

  const raw = line ? line.substring(field.at, field.at + field.length) : '';
  const value = raw.trim();
  const width = Math.min(Math.max(field.length, 3), 26);

  return (
    <div className="flex shrink-0 flex-col gap-1">
      <span className="text-center font-mono text-[11px] text-muted-foreground tabular-nums">
        {field.length === 1 ? field.at + 1 : `${field.at + 1}-${field.at + field.length}`}
      </span>
      <span
        className={cn(
          'rounded border px-1.5 py-1 text-center font-mono text-[13px] whitespace-pre',
          value
            ? 'border-[color:var(--ts-border)] bg-surface text-gray-3'
            : 'border-dashed border-[color:var(--ts-border-soft)] bg-[color:var(--ts-blue-bglayout)] text-muted-foreground',
        )}
        style={{ minWidth: `${width}ch` }}
        title={`${field.label}: positions ${field.at + 1} to ${field.at + field.length}`}
      >
        {value || '·'.repeat(Math.min(field.length, 8))}
      </span>
      <span className="max-w-[26ch] text-center text-[11px] leading-tight text-muted-foreground">{field.label}</span>
      {field.iris ? (
        <span className="max-w-[26ch] text-center text-[11px] leading-tight font-medium text-primary">{field.iris}</span>
      ) : null}
    </div>
  );
}
